// [NEW FILE] PM-15 / PM-17: the manager (while approving goals) and HR (in the final review) can correct an employee's
// goal themselves, with a reason. Shown only when REACT_APP_BACKEND_REVIEWER_GOAL_EDIT=true, because today's backend
// lets only the goal owner edit a goal (the workaround is Request changes / change requests).
import { useState } from "react";
import { toast } from "react-toastify";
import { FiEdit2 } from "react-icons/fi";
import Button from "../common/Button";
import FormField from "../common/FormField";
import ConfirmDialog from "../common/ConfirmDialog";
import GoalFormDrawer from "./GoalFormDrawer";
import { reviewerEditGoal } from "../../services/goalApi";
import { GOAL_CATEGORIES } from "../../services/core";
import { BACKEND } from "../../config/backendFeatures";
import { getErrorMessage } from "../../utils/errors";

export const REVIEWER_GOAL_EDIT = BACKEND.reviewerGoalEdit;

// Returns { action(goal), element }: put action into GoalList renderActions and render element once on the page.
export default function useReviewerGoalEdit({ goals = [], editedAs, employeeName, cycle, enabled = true, onSaved }) {
  const [editing, setEditing] = useState(null);
  const [pendingForm, setPendingForm] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const active = REVIEWER_GOAL_EDIT && enabled;

  const total = goals.reduce((sum, g) => sum + Number(g.weightage || 0), 0);
  const remaining = editing ? 100 - total + Number(editing.weightage || 0) : 0;

  const save = async () => {
    setBusy(true);
    try {
      await reviewerEditGoal(editing, pendingForm, reason);
      toast.success(`Goal updated. ${employeeName || "The employee"} is notified and sees your reason in the history.`);
      setPendingForm(null); setEditing(null); setReason("");
      onSaved?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const action = (goal) => active && (
    <Button size="sm" variant="ghost" icon={FiEdit2} onClick={() => { setEditing(goal); setReason(""); }}>Edit</Button>
  );

  const element = active ? (
    <>
      <GoalFormDrawer
        open={Boolean(editing) && !pendingForm}
        goal={editing}
        cycle={cycle}
        categories={GOAL_CATEGORIES}
        remaining={remaining}
        mode="manager"
        employeeName={employeeName}
        title={`Edit ${employeeName ? `${employeeName}'s` : "the"} goal`}
        subtitle={`You are changing the employee's goal as ${editedAs === "HR" ? "HR" : "their manager"}. You'll give a reason next; the employee is notified.`}
        submitLabel="Next: add reason"
        onClose={() => setEditing(null)}
        onSave={async (form) => { setPendingForm(form); return false; }}
      />
      <ConfirmDialog
        open={Boolean(pendingForm)}
        title="Save this change to the goal?"
        message="The goal sheet keeps its status. The old and new values and your reason are kept in the history."
        confirmLabel="Save change"
        loading={busy}
        onConfirm={save}
        onClose={() => { if (!busy) setPendingForm(null); }}
      >
        <FormField label="Reason for the change" required>
          <textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="For example: target corrected to match the team plan" />
        </FormField>
      </ConfirmDialog>
    </>
  ) : null;

  return { action, element, active };
}
